/**
 * ReassignTaskPanel — supervisor-driven "assign / reassign cleaner"
 * affordance shown on TaskDetailPage. Same component, two label modes:
 *
 *   - First assign: task.assigned_to_user_id IS NULL → "Assign cleaner".
 *     This is the path supervisors use for a freshly-created pending
 *     task that has no cleaner yet. The underlying reassign_cleaning_task
 *     RPC accepts NULL → user-id as a valid swap (IS NOT DISTINCT FROM
 *     correctly treats NULL <> uuid as distinct).
 *   - Reassign: task.assigned_to_user_id IS NOT NULL → "Reassign to a
 *     different cleaner". The dropdown excludes the current assignee.
 *
 * Render rules:
 *   - Visible only to supervisor / contractor_admin / super_admin.
 *   - Visible only when task.status is 'pending' or 'in_progress'.
 *     Terminal-state tasks (completed / approved / rejected) cannot
 *     be reassigned — the RPC refuses with INVALID_STATE, but hiding
 *     the UI keeps the surface clean.
 *
 * Interaction:
 *   - Collapsed: a single "Assign cleaner" / "Reassign" button.
 *   - Expanded: cleaner dropdown, optional reason textarea, Confirm +
 *     Cancel buttons. On success the panel collapses and queries
 *     refresh; on failure the AppError userMessage renders inline
 *     (CLAUDE.md ERROR 4).
 */

import { useState, type ReactNode }              from 'react'
import { useTranslation }                        from 'react-i18next'
import { useReassignTask }                       from '@/hooks/useReassignTask'
import { useOrgCleanersForReassign }             from '@/hooks/useOrgCleanersForReassign'
import { isAppError }                            from '@/lib/errors/AppError'
import type { TaskRow }                          from '@/hooks/useTask'
import type { Role }                             from '@/types/roles'

interface Props {
  task:  TaskRow
  role:  Role   | null
  orgId: string | null
}

const REASSIGNABLE_STATUSES = new Set(['pending', 'in_progress'])
const SUPERVISOR_ROLES      = new Set<Role>(['supervisor', 'contractor_admin', 'super_admin'])
const REASON_MAX            = 500

export function ReassignTaskPanel({ task, role, orgId }: Props): ReactNode {
  const { t } = useTranslation()
  const [expanded, setExpanded]           = useState(false)
  const [pickedCleaner, setPickedCleaner] = useState<string>('')
  const [reason, setReason]               = useState<string>('')

  const reassign = useReassignTask()
  const candidates = useOrgCleanersForReassign(orgId)

  if (role === null || !SUPERVISOR_ROLES.has(role))      return null
  if (!REASSIGNABLE_STATUSES.has(task.status))           return null

  const hasAssignee     = task.assigned_to_user_id !== null
  const dropdownOptions = (candidates.data ?? []).filter((c) => c.user_id !== task.assigned_to_user_id)
  const canSubmit       = pickedCleaner.length > 0 && !reassign.isPending

  function reset(): void {
    setExpanded(false)
    setPickedCleaner('')
    setReason('')
  }

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return
    await reassign.mutateAsync({
      taskId:            task.id,
      newAssigneeUserId: pickedCleaner,
      reason:            reason,
    }).then(reset).catch(() => { /* AppError rendered below */ })
  }

  return (
    <section
      aria-label={hasAssignee
        ? t('reassignTask.panelLabel', 'Reassign task')
        : t('assignTask.panelLabel',   'Assign cleaner to task')}
      style={{
        background:    'var(--brand-neutral-50)',
        border:        '1px solid var(--brand-neutral-200)',
        borderRadius:  'var(--brand-radius-lg)',
        padding:       '0.85rem 1rem',
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.5rem',
      }}
    >
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            padding:      '0.55rem 0.9rem',
            background:   'var(--brand-neutral-200)',
            color:        'var(--brand-neutral-900)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '0.9rem',
            fontWeight:   600,
            cursor:       'pointer',
            alignSelf:    'flex-start',
          }}
        >
          {hasAssignee
            ? t('reassignTask.button', 'Reassign to a different cleaner')
            : t('assignTask.button',   'Assign cleaner')}
        </button>
      )}

      {expanded && (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            {hasAssignee
              ? t('reassignTask.cleanerLabel', 'Hand off to')
              : t('assignTask.cleanerLabel',   'Assign to')}
            <select
              aria-label={hasAssignee
                ? t('reassignTask.cleanerLabel', 'Hand off to')
                : t('assignTask.cleanerLabel',   'Assign to')}
              value={pickedCleaner}
              onChange={(e) => setPickedCleaner(e.target.value)}
              disabled={candidates.isLoading || reassign.isPending}
              style={{
                padding:      '0.5rem 0.7rem',
                border:       '1px solid var(--brand-neutral-300)',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.95rem',
              }}
            >
              <option value="">{t('reassignTask.cleanerPlaceholder', 'Pick a cleaner…')}</option>
              {dropdownOptions.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.full_name}
                </option>
              ))}
            </select>
            {candidates.isLoading && (
              <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
                {t('reassignTask.loadingCleaners', 'Loading cleaners…')}
              </span>
            )}
            {!candidates.isLoading && dropdownOptions.length === 0 && (
              <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
                {t('reassignTask.noCleaners', 'No other cleaners on this organization.')}
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            {t('reassignTask.reasonLabel', 'Reason (optional)')}
            <textarea
              aria-label={t('reassignTask.reasonLabel', 'Reason (optional)')}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
              maxLength={REASON_MAX}
              rows={2}
              placeholder={t('reassignTask.reasonPlaceholder', 'e.g. cleaner went home sick')}
              disabled={reassign.isPending}
              style={{
                padding:      '0.5rem 0.7rem',
                border:       '1px solid var(--brand-neutral-300)',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.9rem',
                resize:       'vertical',
              }}
            />
            <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.75rem', alignSelf: 'flex-end' }}>
              {reason.length}/{REASON_MAX}
            </span>
          </label>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => { void onSubmit() }}
              disabled={!canSubmit}
              style={{
                padding:      '0.6rem 1rem',
                background:   canSubmit ? 'var(--brand-primary)' : 'var(--brand-neutral-300)',
                color:        'var(--brand-white)',
                border:       'none',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.95rem',
                fontWeight:   600,
                cursor:       canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {reassign.isPending
                ? (hasAssignee
                    ? t('reassignTask.submitting', 'Reassigning…')
                    : t('assignTask.submitting',   'Assigning…'))
                : (hasAssignee
                    ? t('reassignTask.confirm',    'Confirm reassign')
                    : t('assignTask.confirm',      'Confirm assignment'))}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={reassign.isPending}
              style={{
                padding:      '0.6rem 1rem',
                background:   'transparent',
                color:        'var(--brand-neutral-700)',
                border:       '1px solid var(--brand-neutral-300)',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.95rem',
                fontWeight:   500,
                cursor:       reassign.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {t('reassignTask.cancel', 'Cancel')}
            </button>
          </div>

          {reassign.error && isAppError(reassign.error) && (
            <p
              role="alert"
              style={{
                margin:       0,
                padding:      '0.5rem 0.75rem',
                background:   'var(--brand-danger-subtle)',
                color:        'var(--brand-danger)',
                border:       '1px solid var(--brand-danger)',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.85rem',
              }}
            >
              {reassign.error.userMessage}
            </p>
          )}
        </>
      )}
    </section>
  )
}
